import { useEffect, useRef, useState } from "react";
import { AD_SLOTS, ADSENSE_CLIENT, loadAdSense, pushAd, type AdSlotName } from "@/lib/adsense";
import { readStoredConsent } from "@/lib/analytics";
import { useT } from "@/i18n";


/** Tracks the visitor's cookie choice, reacting to the banner without a reload. */
export function useAdConsent() {
  const [granted, setGranted] = useState(false);

  useEffect(() => {
    const read = () => setGranted(readStoredConsent() === "granted");
    read();
    window.addEventListener("behot:consent", read);
    window.addEventListener("storage", read);
    return () => {
      window.removeEventListener("behot:consent", read);
      window.removeEventListener("storage", read);
    };
  }, []);

  return granted;
}

/** Loads the AdSense tag (Auto ads included) once cookies are accepted. */
export function AdSenseProvider() {
  const granted = useAdConsent();
  useEffect(() => {
    if (granted) loadAdSense();
  }, [granted]);
  return null;
}

/**
 * Native in-feed ad card. Uses the site's own card styling so the unit sits
 * inside the board flow instead of looking like a banner. Renders nothing when
 * the slot is not configured yet or the visitor declined cookies.
 */
export function NativeAdCard({
  slot,
  className = "",
  label,
}: {
  slot: AdSlotName;
  className?: string;
  label?: string;
}) {
  const { t } = useT();
  const granted = useAdConsent();
  const { id: slotId, layoutKey } = AD_SLOTS[slot];
  const ref = useRef<HTMLModElement | null>(null);
  const pushed = useRef(false);

  useEffect(() => {
    if (!granted || !slotId || pushed.current) return;
    pushed.current = true;
    pushAd();
  }, [granted, slotId]);

  if (!granted || !slotId) return null;

  return (
    <div
      className={`card-surface overflow-hidden p-3 sm:p-4 ${className}`}
      role="complementary"
      aria-label={label ?? t("shell.ads.sponsored")}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {label ?? t("shell.ads.sponsored")}
        </span>
      </div>
      <ins
        ref={ref}
        className="adsbygoogle block"
        style={{ display: "block" }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slotId}
        data-ad-format="fluid"
        {...(layoutKey
          ? { "data-ad-layout-key": layoutKey }
          : { "data-ad-layout": "in-article" })}
        data-full-width-responsive="true"
      />
    </div>
  );
}

